import {
  filter,
  tap,
  ignoreElements,
  switchMap,
  mergeMap,
  catchError,
  map,
} from 'rxjs/operators'
import {of, concat, from} from 'rxjs'
import {Epic} from 'redux-observable'
import {RootState, AppActions} from 'app/rootReducer'
import {SettingsService} from 'services/api'
import {settingsActions} from '../settingsSlice'
import Toast from 'components/Basic/Toast'
// Removed circular dependency - these actions are handled elsewhere

const getLinkEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.getLink.match),
    switchMap(() =>
      from(SettingsService.getLink()).pipe(
        mergeMap(({data}) => [settingsActions.getLinkSuccess(data)]),
        catchError((message: string) =>
          concat(of(settingsActions.getLinkFailure(message))),
        ),
      ),
    ),
  )

const getLinkFailureEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.getLinkFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

/* eslint-disable @typescript-eslint/no-explicit-any */
const getImportStatusEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.getImportStatus.match),
    switchMap(() =>
      from(SettingsService.getImportStatus()).pipe(
        mergeMap(({data}) => {
          const actions = [settingsActions.getImportStatusSuccess(data)]
          // Note: User import status actions are handled in the main user epics
          // to avoid circular dependencies
          return actions
        }),
        catchError((message: string) =>
          concat(of(settingsActions.getImportStatusFailure(message))),
        ),
      ),
    ),
  )

const getImportStatusFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.getImportStatusFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )
const getSearchUsersGroupsEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.getSearchUsersGroups.match),
    switchMap(({payload}) =>
      from(SettingsService.getSearchUsersGroups(payload)).pipe(
        mergeMap(({data}) => [
          settingsActions.getSearchUsersGroupsSuccess(data),
        ]),
        catchError((message: string) =>
          concat(of(settingsActions.getSearchUsersGroupsFailure(message))),
        ),
      ),
    ),
  )

const getSearchUsersGroupsFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.getSearchUsersGroupsFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

const getUserGroupsEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.getGroupsUsers.match),
    mergeMap(() =>
      from(SettingsService.getImportStatus()).pipe(
        switchMap(({data: importStatusRes}) =>
          importStatusRes
            ? from(SettingsService.getGroups({qs: {}})).pipe(
                map(({data: groups}) => ({
                  updated_at: importStatusRes,
                  groups,
                })),
              )
            : from([importStatusRes]),
        ),
        mergeMap(({updated_at, groups}) =>
          updated_at.lastUpdated
            ? [
                settingsActions.getGroupsUsersSuccess({
                  importStatus: updated_at,
                  users: {
                    message: 'unsuccessful',
                  },
                  groups,
                }),
              ]
            : [
                settingsActions.getGroupsUsersSuccess({
                  importStatus: updated_at,
                  users: {
                    message: 'unsuccessful',
                  },
                  groups: {
                    message: 'unsuccessful',
                  },
                }),
                settingsActions.getLink(),
              ],
        ),
        catchError((message: string) =>
          concat(of(settingsActions.getGroupsUsersFailure(message))),
        ),
      ),
    ),
  )

const getGroupsEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.getGroups.match),
    switchMap(({payload}) =>
      from(SettingsService.getGroups(payload)).pipe(
        mergeMap(({data}) => [settingsActions.getGroupsSuccess(data)]),
        catchError((message: string) =>
          concat(of(settingsActions.getGroupsFailure(message))),
        ),
      ),
    ),
  )

const getGroupsFailureEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.getGroupsFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

const getUsersEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.getUsers.match),
    switchMap(({payload}) =>
      from(SettingsService.getUsers(payload)).pipe(
        mergeMap(({data}) => [settingsActions.getUsersSuccess(data)]),
        catchError((message: string) =>
          concat(of(settingsActions.getUsersFailure(message))),
        ),
      ),
    ),
  )

const getUsersFailureEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(settingsActions.getUsersFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

const pollingUsersGroupsEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.startPollingGroupsUsers.match),
    mergeMap(() =>
      from(SettingsService.getImportStatus()).pipe(
        switchMap(({data: updated_at}) =>
          updated_at
            ? from(SettingsService.getGroups({qs: {}})).pipe(
                map(({data: groups}) => ({updated_at, groups})),
              )
            : from([updated_at]),
        ),
        mergeMap(({updated_at, groups}) =>
          updated_at
            ? [
                settingsActions.getGroupsUsersSuccess({
                  importStatus: updated_at,
                  users: {
                    message: 'unsuccessful',
                  },
                  groups,
                }),
              ]
            : [
                settingsActions.getGroupsUsersSuccess({
                  importStatus: updated_at,
                  users: {
                    message: 'unsuccessful',
                  },
                  groups: {
                    message: 'unsuccessful',
                  },
                }),
                settingsActions.getLink(),
              ],
        ),
        catchError((message: string) =>
          concat(of(settingsActions.getGroupsUsersFailure(message))),
        ),
      ),
    ),
  )

const getUserGroupDetailsEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.getUserGroupDetails.match),
    switchMap(({payload}) =>
      from(SettingsService.getUserGroupDetails(payload)).pipe(
        mergeMap(({data: {response}}) => [
          settingsActions.getUserGroupDetailsSuccess(
            (response && response[payload]) || {},
          ),
        ]),
        catchError((message: string) =>
          concat(of(settingsActions.getUserGroupDetailsFailure(message))),
        ),
      ),
    ),
  )

const getUserGroupDetailsFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(settingsActions.getUserGroupDetailsFailure.match),
    tap(() =>
      Toast({message: 'Error, unable to fetch user groups. ', type: 'error'}),
    ),
    ignoreElements(),
  )

export const userEpics = [
  getLinkEpic,
  getLinkFailureEpic,
  pollingUsersGroupsEpic,
  getImportStatusEpic,
  getImportStatusFailureEpic,
  getSearchUsersGroupsEpic,
  getSearchUsersGroupsFailureEpic,
  getGroupsEpic,
  getUserGroupsEpic,
  getUsersEpic,
  getUsersFailureEpic,
  getGroupsFailureEpic,
  getUserGroupDetailsEpic,
  getUserGroupDetailsFailureEpic,
]
